import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  normalizarParaBusca,
  type ProdutoNaTabela,
  type ProdutoParaRepor,
} from '@arena/shared/schemas';
import { dinheiro } from '../agenda/formatos.js';
import { mensagemDoErro } from '../api/cliente.js';
import { estoqueApi } from '../api/estoque.js';
import { useSessao } from '../auth/SessaoContext.jsx';
import { Erro, EsqueletoDeCard, EsqueletoDeTabela } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import {
  Botao,
  Card,
  Feed,
  ItemDeFeed,
  Nota,
  Segmentado,
  Status,
  Tabela,
  Vazio,
} from '../ui/primitivos.jsx';
import { AbaDeEntradas } from './AbaDeEntradas.jsx';
import { AbaDeInventarios } from './AbaDeInventarios.jsx';
import {
  ABAS_DO_ESTOQUE,
  SITUACAO_NA_TELA,
  lerAbaDoEstoque,
  margemNaTabela,
  rotuloDaSituacao,
  type AbaDoEstoque,
} from './formatos.js';
import { ModalDeEntrada } from './ModalDeEntrada.jsx';
import { ModalDeInventario } from './ModalDeInventario.jsx';
import { ModalDeProduto } from './ModalDeProduto.jsx';
import { produtoDaUrl } from './produto.js';

/**
 * `#v-estoque` — a tabela de produtos e a reposição sugerida.
 *
 * As colunas **Custo** e **Margem** só aparecem para quem o servidor
 * mandou o custo. Não é a tela escondendo: o dado NÃO VEIO — a projeção
 * do documento 03 tirou a coluna do `SELECT`. O cabeçalho some junto,
 * porque uma coluna de traços ensina a pessoa que o sistema está
 * quebrado, e não que ela não tem permissão.
 *
 * Três abas — **Produtos** (a tabela e a reposição), **Entradas** (o
 * histórico de compras) e **Inventários** (as contagens) —, e a aba mora na
 * URL (`?aba=entradas`): o link
 * colado abre a mesma aba. Trocar de aba usa `replace`, então o voltar do
 * navegador sai da página, não volta à aba anterior. Mais uma aba é
 * mais uma linha em `ABAS_DO_ESTOQUE` e o conteúdo dela aqui embaixo.
 *
 * Ver: Arena_backend/Docs/plano/13-estoque-comandas.md
 */
export function PaginaDoEstoque() {
  const { pode } = useSessao();
  const [params, setParams] = useSearchParams();
  const aba = lerAbaDoEstoque(params);
  const [busca, setBusca] = useState('');
  const [entrada, setEntrada] = useState(false);
  const [inventario, setInventario] = useState(false);
  const [novoProduto, setNovoProduto] = useState(false);
  // O produto aberto mora na URL (`?produto=<id>`), como a aba: o link colado
  // abre o mesmo produto, e fechar o modal o tira do endereço.
  const produtoAberto = produtoDaUrl(params);
  const comInativos = params.get('inativos') === '1';

  const produtos = useQuery({
    queryKey: comInativos ? ['estoque', 'produtos', 'com-inativos'] : ['estoque', 'produtos'],
    queryFn: () => estoqueApi.produtos(comInativos),
  });
  const reposicao = useQuery({ queryKey: ['estoque', 'reposicao'], queryFn: estoqueApi.reposicao });

  const comCusto = produtos.data?.some((p) => typeof p.custo === 'number') ?? false;
  const filtrados = useMemo(() => filtrar(produtos.data ?? [], busca), [produtos.data, busca]);
  const abaixoDoMinimo = reposicao.data?.length ?? 0;

  function trocarDeAba(nova: AbaDoEstoque) {
    const novos = new URLSearchParams(params);
    // A primeira aba é a de quando não há `?aba=`: o endereço limpo.
    if (nova === ABAS_DO_ESTOQUE[0].valor) novos.delete('aba');
    else novos.set('aba', nova);
    setParams(novos, { replace: true });
  }

  function mudar(mudanca: (p: URLSearchParams) => void) {
    const novos = new URLSearchParams(params);
    mudanca(novos);
    setParams(novos, { replace: true });
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Estoque</h1>
          <p>
            {produtos.data?.length ?? 0} produtos cadastrados · {abaixoDoMinimo} abaixo do estoque
            mínimo.
          </p>
        </div>
        {pode('estoque', 'criar') && (
          <div className="head-actions">
            <Botao variante="contorno" icone="plus" onClick={() => setNovoProduto(true)}>
              Novo produto
            </Botao>
            <Botao variante="contorno" onClick={() => setInventario(true)}>
              Inventário
            </Botao>
            <Botao icone="plus" onClick={() => setEntrada(true)}>
              Entrada de mercadoria
            </Botao>
          </div>
        )}
      </div>

      <Nota>
        <b>Estoque.</b> Cada entrada de mercadoria gera a saída financeira correspondente, e cada
        comanda fechada dá baixa automática. O produto abaixo do mínimo entra na lista de reposição
        sugerida.
      </Nota>

      <div style={{ marginBottom: 16 }}>
        <Segmentado
          aria-label="Estoque"
          valor={aba}
          onChange={trocarDeAba}
          opcoes={ABAS_DO_ESTOQUE}
        />
      </div>

      {aba === 'entradas' ? (
        <AbaDeEntradas
          onRegistrar={pode('estoque', 'criar') ? () => setEntrada(true) : undefined}
        />
      ) : aba === 'inventarios' ? (
        <AbaDeInventarios />
      ) : (
        <div className="grid g-2-1">
          <Card
            titulo="Produtos"
            acao={
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
                  <input
                    type="checkbox"
                    checked={comInativos}
                    onChange={(e) =>
                      mudar((p) => (e.target.checked ? p.set('inativos', '1') : p.delete('inativos')))
                    }
                  />
                  Mostrar inativos
                </label>
                <label className="search" style={{ maxWidth: 240 }}>
                  <Icone nome="search" />
                  <input
                    value={busca}
                    onChange={(e) => setBusca(e.target.value)}
                    placeholder="Buscar produto"
                    aria-label="Buscar produto"
                  />
                </label>
              </div>
            }
          >
            {produtos.isPending ? (
              <EsqueletoDeTabela colunas={comCusto ? 8 : 6} />
            ) : produtos.isError ? (
              <Erro
                detalhe={mensagemDoErro(produtos.error)}
                onTentarDeNovo={() => void produtos.refetch()}
              />
            ) : filtrados.length === 0 ? (
              <Vazio>Nenhum produto encontrado.</Vazio>
            ) : (
              <Tabela
                colunas={[
                  'Produto',
                  'Categoria',
                  ...(comCusto ? ['Custo'] : []),
                  'Venda',
                  ...(comCusto ? ['Margem'] : []),
                  'Estoque',
                  'Mínimo',
                  'Situação',
                ]}
              >
                {filtrados.map((produto) => (
                  <tr
                    key={produto.id}
                    className="clickable"
                    tabIndex={0}
                    aria-label={`Abrir ${produto.nome}`}
                    onClick={() => mudar((p) => p.set('produto', produto.id))}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        mudar((p) => p.set('produto', produto.id));
                      }
                    }}
                  >
                    <td>
                      {produto.nome}
                      {!produto.ativo && (
                        <>
                          {' '}
                          <Status situacao="neutra">Inativo</Status>
                        </>
                      )}
                    </td>
                    <td>{produto.categoria}</td>
                    {comCusto && (
                      <td className="num">
                        {produto.custo === undefined ? '—' : dinheiro(produto.custo)}
                      </td>
                    )}
                    <td className="num">{dinheiro(produto.preco)}</td>
                    {comCusto && <td className="num">{margemNaTabela(produto)}</td>}
                    <td className="num">{produto.saldo}</td>
                    <td className="num">{produto.estoqueMinimo}</td>
                    <td>
                      <Status situacao={SITUACAO_NA_TELA[produto.situacao]}>
                        {rotuloDaSituacao(produto.situacao)}
                      </Status>
                    </td>
                  </tr>
                ))}
              </Tabela>
            )}
          </Card>

          <Card
            titulo="Reposição sugerida"
            acao={
              abaixoDoMinimo > 0 ? (
                <Status situacao="atrasada">
                  {abaixoDoMinimo} {abaixoDoMinimo === 1 ? 'item' : 'itens'}
                </Status>
              ) : undefined
            }
          >
            {reposicao.isPending ? (
              <EsqueletoDeCard linhas={3} />
            ) : reposicao.isError ? (
              <Erro
                detalhe={mensagemDoErro(reposicao.error)}
                onTentarDeNovo={() => void reposicao.refetch()}
              />
            ) : reposicao.data.length === 0 ? (
              <Vazio>Nada abaixo do mínimo. O estoque está em dia.</Vazio>
            ) : (
              <Feed>
                {reposicao.data.map((produto) => (
                  <LinhaDeReposicao key={produto.id} produto={produto} />
                ))}
              </Feed>
            )}

            {pode('estoque', 'criar') && reposicao.data && reposicao.data.length > 0 && (
              <Botao style={{ width: '100%', marginTop: 14 }} onClick={() => setEntrada(true)}>
                Registrar entrada
              </Botao>
            )}
          </Card>
        </div>
      )}

      {entrada && <ModalDeEntrada onFechar={() => setEntrada(false)} />}
      {inventario && <ModalDeInventario onFechar={() => setInventario(false)} />}
      {novoProduto && <ModalDeProduto produtoId={null} onFechar={() => setNovoProduto(false)} />}
      {produtoAberto && (
        <ModalDeProduto
          produtoId={produtoAberto}
          onFechar={() => mudar((p) => p.delete('produto'))}
        />
      )}
    </>
  );
}

function LinhaDeReposicao({ produto }: { produto: ProdutoParaRepor }) {
  return (
    <ItemDeFeed
      marca={<span className="sq" style={{ background: 'var(--red)' }} aria-hidden="true" />}
      titulo={produto.nome}
      subtitulo={`${produto.saldo} em estoque · mínimo ${produto.estoqueMinimo}`}
      direita={
        <>
          <b className="num">{produto.sugestao} un</b>
          <small>sugestão de compra</small>
        </>
      }
    />
  );
}

/**
 * O filtro da caixa de busca, no cliente.
 *
 * É a única busca desta tela que NÃO vai ao servidor, e de propósito: a
 * tabela inteira já está na mão, são dezenas de linhas, e uma ida ao
 * servidor a cada tecla só acrescentaria latência. A busca do servidor
 * (com trigram) é a de `#m-comanda`, onde a lista não está carregada.
 */
function filtrar(produtos: readonly ProdutoNaTabela[], busca: string): ProdutoNaTabela[] {
  const termo = normalizarParaBusca(busca).trim();
  if (!termo) return [...produtos];
  return produtos.filter((produto) => normalizarParaBusca(produto.nome).includes(termo));
}
